import toInteger from 'lodash/toInteger'
import moment from 'moment'
import {
  path,
  pipe,
  equals,
  set,
  reduce,
  lensProp,
  lensPath,
  dissocPath,
  reject,
  defaultTo,
  append,
  uniq,
  mergeLeft,
  mergeDeepLeft,
  assoc,
  keys,
  dissoc,
  prop,
  head,
  propOr,
  pathOr,
  curry,
  evolve,
  omit,
  assocPath
} from 'ramda'
import { batch } from 'react-redux'
import { isEmpty } from 'lodash'
import config from '@/config'
import selectorBuilder from '@/models/selector-builder'
import { headObj, isNotEmpty, callOr } from '@/utils'
import { downloadBlob } from '@/utils/files'
import isMobile from '@/utils/is-mobile'
import { stringifySearchParams } from '@/hooks/use-search-params'
import {
  timelinesSelector,
  timelinesSearchSelector,
  timelineSelector,
  timelineEventsSelector,
  callbackUserSelector,
  listingTimelinesSelector,
  tenantTimelinesSelector
} from './selectors'
import {
  getTimelines,
  getNextTimelines,
  getEventsUp,
  getEventsDown,
  getSecureFile,
  sendMessage,
  sendInviteToApply,
  sendImage,
  sendFile,
  getStats,
  setTimelineRead,
  tenantStartChat,
  deleteTimeline,
  addTimelineToFavourites,
  removeTimelineToFavourites,
  reportProblem,
  sendListingVerificationRequest,
  sendIdVerificationRequest,
  hidePhoneNumber,
  unhidePhoneNumber,
  getTimelinesQueryString,
  getNextTimelinesQueryString,
  loadTimeline,
  bulkTimelineMarkAsRead,
  bulkTimelineDelete
} from './ports'

const omitTimelines = omit(['timelines'])

if ('Notification' in window) {
  if (Notification.permission !== 'denied' || Notification.permission !== 'granted') {
    Notification.requestPermission()
  }
}

const INITIAL_STATE = {
  intialTimelinesLoaded: false,
  page: 1
}

const patchUsers = curry((users, state) =>
  set(lensProp('users'), pipe(prop('users'), mergeLeft(users))(state))(state)
)

const chat = {
  state: INITIAL_STATE,
  reducers: {
    clear: () => INITIAL_STATE,
    clearChat: (state, timelineRoom) => set(lensPath(['events', timelineRoom]), {}, state),
    addToFavourites: (state, timelineRoom) =>
      pipe(
        set(lensPath(['current_timeline', 'is_favourite']), '1'),
        set(lensPath(['timelines', timelineRoom, 'is_favourite']), '1')
      )(state),
    removeFromFavourites: (state, timelineRoom) =>
      pipe(
        set(lensPath(['current_timeline', 'is_favourite']), '0'),
        set(lensPath(['timelines', timelineRoom, 'is_favourite']), '0')
      )(state),

    setCurrentTimeline: (state, payload) => assoc('current_timeline', payload)(state),
    clearCurrentChat: dissoc('currentTimelineId'),
    setSearchState: (state, payload) => assoc('searchState', payload)(state),
    setCurrentTimelineLandlords: (state, payload) =>
      assocPath(['current_timeline', 'user_ids', 'us'], payload.user_ids)(state),
    clearSearchState: pipe(dissoc('searchState')),
    goingOutOfSearchState: state =>
      state.current_timeline
        ? pipe(
            dissoc('intialTimelinesLoaded'),
            dissocPath(['current_timeline', 'is_search_result'])
          )(state)
        : state,
    remove: (state, at) => dissocPath(at, state),
    setTimelines: (state, payload) =>
      pipe(
        assoc('intialTimelinesLoaded', prop('intialTimelinesLoaded', payload)),
        assoc('page', propOr(state.page, 'page')(payload)),
        mergeLeft({
          users: mergeLeft(state.users, payload.users),
          timelines: payload.timelines,
          events: payload.replaceEvents
            ? payload.timeline_events2
            : payload.isSearch
              ? state.events
              : mergeLeft(state.events, payload.timeline_events2),
          eventsSearch: payload.isSearch ? payload.timeline_events2 : {},
          lastEvents: payload.timeline_events2
        })
      )(state),
    saveTimelines: (state, payload) =>
      pipe(
        assoc('intialTimelinesLoaded', prop('intialTimelinesLoaded', payload)),
        assoc('page', propOr(state.page, 'page')(payload)),
        mergeDeepLeft({
          users: payload.users,
          timelines: payload.timelines || state.timelines,
          events: payload.replaceEvents
            ? payload.timeline_events2
            : payload.isSearch
              ? state.events
              : mergeLeft(state.events, payload.timeline_events2),
          eventsSearch: payload.isSearch
            ? mergeLeft(state.eventsSearch, payload.timeline_events2)
            : {},
          lastEvents: payload.timeline_events2
        })
      )(state),
    updateTimeline: (state, payload) =>
      set(lensPath(['timelines', payload.timeline_room]), payload, state),
    updateApplications: (state, timelineRoom) =>
      set(
        lensPath(['timelines', timelineRoom, 'applications']),
        {
          id: null,
          user_id: null
        },
        state
      ),
    updateApplication: (state, payload) => {
      return set(
        lensPath(['timelines', payload.timeline_room, 'applications']),
        {
          id: payload.id,
          user_id: payload.user_id
        },
        state
      )
    },
    patchUsers: (state, payload) => patchUsers(payload, state),
    saveEvents: (state, payload) => {
      return pipe(
        patchUsers(propOr({}, 'users', payload)),
        set(
          lensPath(['current_timeline', 'lastEventLoadedUp']),
          payload.lastEventIdUp || path(['current_timeline', 'lastEventLoadedUp'], state)
        ),
        set(
          lensPath(['current_timeline', 'lastEventLoadedDown']),
          payload.lastEventIdDown || path(['current_timeline', 'lastEventLoadedDown'], state)
        ),
        set(
          lensPath(['events', payload.timelineRoomId]),
          pipe(
            path(['events', payload.timelineRoomId]),
            defaultTo({}),
            mergeLeft(pathOr({}, ['timeline_events2', payload.timelineRoomId], payload))
          )(state)
        ),
        assoc('currentTimelineId', payload.timelineRoomId)
      )(state)
    },
    updateLastEvent: (state, payload) => {
      const timelineId = pipe(headObj, prop('timeline_room'))(payload)
      return set(lensPath(['lastEvents', timelineId]), payload, state)
    },
    updateEvents: (state, payload) => {
      const payloadData = headObj(payload)
      const eventId = head(keys(payload))
      const timelineId = pipe(headObj, prop('timeline_room'))(payload)

      return set(
        lensPath(['events', timelineId, eventId]),
        pipe(path(['events', timelineId, eventId]), defaultTo({}), mergeLeft(payloadData))(state),
        state
      )
    },
    replaceEvents: (state, payload) => {
      return set(lensPath(['events', payload.timelineId]), payload.events, state)
    },
    removePhantom: (state, { timelineRoomId }) => {
      if (!timelineRoomId) return state
      const room = state.events?.[timelineRoomId] || []
      const events = reject(e => e.pending, room)
      return {
        ...state,
        events: {
          ...state.events,
          [timelineRoomId]: events
        }
      }
    },
    saveUnread: (state, payload) => ({
      ...state,
      ...payload
    }),
    setRead: (state, payload) => {
      const removeFrom = curry((fields, data) =>
        reduce(
          (acc, value) =>
            set(
              lensProp(value),
              pipe(
                path([value]),
                defaultTo([]),
                reject(a => a === payload.last_read.timeline_room)
              )(acc)
            )(acc),
          data,
          fields
        )
      )
      return pipe(
        removeFrom(['unread_timeline_rooms']),
        set(
          lensPath(['timelines', payload.last_read.timeline_room]),
          pipe(
            path(['timelines', payload.last_read.timeline_room]),
            defaultTo({}),
            mergeLeft(payload.last_read)
          )(state)
        )
      )(state)
    },
    setTimelineEventsRead: (state, payload, room) => {
      const payloadArray = Object.entries(payload).map(x => ({ [x[0]]: x[1] }))
      const setEventRead = (state, item) => {
        const itemDataObj = headObj(headObj(item).data)
        return set(lensPath(['events', room, itemDataObj.id]), itemDataObj)(state)
      }
      return reduce(setEventRead, state, payloadArray)
    },
    setUnread: (state, message) => {
      const fields = ['unread_timeline_rooms']
      return reduce(
        (acc, value) =>
          set(
            lensProp(value),
            pipe(path([value]), defaultTo([]), append(message.timeline_room), uniq)(acc)
          )(acc),
        state,
        fields
      )
    }
  },
  effects: dispatch => ({
    getTimelines: async (_, rootState) => {
      // if (rootState.chat.intialTimelinesLoaded) return
      // const wasSearchResult = rootState.chat?.current_timeline?.is_search_result
      dispatch.supportRoom.getSupportRoom()
      try {
        dispatch.chat.clearSearchState()
        const { body, response } = await getTimelines()
        if (!response.ok) {
          throw Error(response.body.message || response.statusText)
        }
        dispatch.chat.setTimelines({
          ...body,
          intialTimelinesLoaded: true,
          replaceEvents: false,
          source: 'getTimelines'
        })
      } catch (error) {
        console.log('[Chat/getTimelines]', error)
      }
    },
    async getNextTimelines(payload, rootState) {
      const page = rootState.chat.page

      try {
        const { body, response } = await getNextTimelines({
          pageNumber: page + 1
        })
        if (response.ok && isNotEmpty(body?.timelines)) {
          dispatch.chat.saveTimelines({
            ...body,
            page: page + 1,
            intialTimelinesLoaded: true
          })
        }
      } catch (error) {
        console.log('[Chat/getNextTimelines]', error)
      }
    },
    getEventsUp: async (payload, rootState) => {
      try {
        if (
          equals(
            payload.lastEventId,
            path(['chat', 'current_timeline', 'lastEventLoadedUp'], rootState)
          )
        )
          return

        const { response, body } = await getEventsUp({
          timelineRoomId: payload.timelineRoomId,
          lastEventId: payload.lastEventId
        })

        if (response.ok) {
          dispatch.chat.saveEvents({
            ...body,
            timelineRoomId: payload.timelineRoomId,
            lastEventIdUp: payload.lastEventId,
            source: 'getEventsUp'
          })
        }
      } catch (error) {
        console.log('[Chat/getEventsUp]', error)
      }
    },
    getEventsDown: async (payload, rootState) => {
      try {
        if (
          equals(
            payload.lastEventId,
            path(['chat', 'current_timeline', 'lastEventLoadedDown'], rootState)
          )
        )
          return

        const { response, body } = await getEventsDown({
          timelineRoomId: payload.timelineRoomId,
          lastEventId: payload.lastEventId
        })

        if (response.ok) {
          dispatch.chat.saveEvents({
            ...body,
            timelineRoomId: payload.timelineRoomId,
            lastEventIdDown: payload.lastEventId,
            source: 'getEventsDown'
          })
        }
      } catch (error) {
        console.log('[Chat/getEventsDown]', error)
      }
    },
    getEventsSearchUp: async (payload, rootState) => {
      try {
        if (
          equals(
            payload.lastEventId,
            path(['chat', 'current_timeline', 'lastEventLoadedUp'], rootState)
          )
        )
          return

        const { response, body } = await getEventsUp({
          timelineRoomId: payload.timelineRoomId,
          lastEventId: payload.lastEventId
        })

        if (response.ok) {
          dispatch.chat.saveEvents({
            ...body,
            timelineRoomId: payload.timelineRoomId,
            lastEventIdUp: payload.lastEventId,
            source: 'getEventsSearchUp'
          })
        }
      } catch (error) {
        console.log('[Chat/getEventsSearchUp]', error)
      }
    },
    getEventsSearchDown: async (payload, rootState) => {
      try {
        if (
          equals(
            payload.lastEventId,
            path(['chat', 'current_timeline', 'lastEventLoadedDown'], rootState)
          )
        )
          return

        const { response, body } = await getEventsDown({
          timelineRoomId: payload.timelineRoomId,
          lastEventId: payload.lastEventId
        })

        if (response.ok) {
          dispatch.chat.saveEvents({
            ...body,
            timelineRoomId: payload.timelineRoomId,
            lastEventIdDown: payload.lastEventId,
            source: 'getEventsSearchDown'
          })
        }
      } catch (error) {
        console.log('[Chat/getEventsSearchDown]', error)
      }
    },
    async setTimelineRead(timelineRoomId, rootState) {
      if (!rootState.chat.timelines?.[timelineRoomId]) return
      const { body, response } = await setTimelineRead({ timelineRoomId })
      if (response.ok) {
        dispatch.chat.setRead(body)
      }
    },
    getSecureFile: async ({ fileId, fileName }) => {
      try {
        const response = await getSecureFile({ fileId })
        if (response.response.ok) {
          downloadBlob(response.body, fileName)
        }
      } catch (error) {
        console.log('[Chat/getSecureFile]', error)
      }
    },
    sendMessage: async payload => {
      payload.passthrough = Date.now()
      dispatch.chat.addPhantomEvent(payload)
      try {
        const { response, body } = await sendMessage(
          { message: payload.message, passthrough: payload.passthrough },
          { timelineRoomId: payload.timelineRoomId }
        )
        if (response.ok) {
          delete headObj(body.data).is_read
          batch(() => {
            dispatch.chat.removePhantom(payload)
            dispatch.chat.updateEvents(body.data)
            dispatch.chat.updateLastEvent(body.data)
          })
        }
      } catch (error) {
        console.log('[Chat/sendMessage]', error)
      }
    },
    inviteToApply: async payload => {
      try {
        const { response, body } = await sendInviteToApply(undefined, {
          timelineRoomId: payload.timelineRoomId
        })
        if (!response.ok) {
          throw Error(response.body.message || response.statusText)
        }
        const timelineEventData = body.timeline_event.data
        batch(() => {
          dispatch.chat.updateLastEvent(timelineEventData)
          dispatch.chat.updateEvents(timelineEventData)
        })
        return { response, body }
      } catch (error) {
        console.log('[Chat/sendInviteToApply]', error)
      }
    },
    hidePhoneNumber: async () => {
      try {
        const { response, body } = await hidePhoneNumber()
        if (!response.ok) {
          throw Error(response.body.message || response.statusText)
        }
        return { response, body }
      } catch (error) {
        console.log('[Chat/hidePhoneNumber]', error)
      }
    },
    unhidePhoneNumber: async () => {
      try {
        const { response, body } = await unhidePhoneNumber()
        if (!response.ok) {
          throw Error(response.body.message || response.statusText)
        }
        return { response, body }
      } catch (error) {
        console.log('[Chat/unhidePhoneNumber]', error)
      }
    },
    requestIdVerification: async payload => {
      try {
        const { response, body } = await sendIdVerificationRequest(undefined, {
          timelineRoomId: payload.timelineRoomId
        })
        if (!response.ok) {
          throw Error(response.body.message || response.statusText)
        }
        return { response, body }
      } catch (error) {
        console.log('[Chat/requestIdVerification]', error)
      }
    },
    requestListingVerification: async payload => {
      try {
        const { response, body } = await sendListingVerificationRequest(undefined, {
          timelineRoomId: payload.timelineRoomId
        })
        if (!response.ok) {
          throw Error(response.body.message || response.statusText)
        }
        return { response, body }
      } catch (error) {
        console.log('[Chat/requestListingVerification]', error)
      }
    },
    addPhantomEvent(payload, rootState) {
      const message = {
        [payload.passthrough]: {
          id: payload.passthrough,
          event_type: 'chat',
          pending: true,
          timeline_room: payload.timelineRoomId,
          event_info: {
            message: payload.message
          },
          created_at: moment.utc().format(),
          user_id: rootState.session.session.id
        }
      }
      batch(() => {
        dispatch.chat.updateLastEvent(message)
        dispatch.chat.updateEvents(message)
      })
    },
    sendFile: async payload => {
      try {
        const { response, body } = await sendFile(
          { file: payload.file },
          { timelineRoomId: payload.timelineRoomId }
        )
        if (response.ok) {
          batch(() => {
            dispatch.chat.updateLastEvent(body.data)
            dispatch.chat.updateEvents(body.data)
          })
        }
      } catch (error) {
        console.log('[Chat/sendFile]', error)
      }
    },
    sendImage: async payload => {
      try {
        const { response, body } = await sendImage(
          { file: payload.file },
          { timelineRoomId: payload.timelineRoomId }
        )
        if (response.ok) {
          batch(() => {
            dispatch.chat.updateLastEvent(body.data)
            dispatch.chat.updateEvents(body.data)
          })
        }
      } catch (error) {
        console.log('[Chat/sendImage]', error)
      }
    },
    deleteTimeline: async timelineRoomId => {
      try {
        const { response, body } = await deleteTimeline(undefined, {
          timelineRoomId
        })
        if (!response.ok) {
          throw Error(response.body.message || response.statusText)
        }
        dispatch.chat.remove(['timelines', timelineRoomId])
        return { response, body }
      } catch (error) {
        return { error }
      }
    },
    addTimelineToFavourites: async timelineRoomId => {
      try {
        const { response, body } = await addTimelineToFavourites(undefined, {
          timelineRoomId
        })
        if (!response.ok) {
          throw Error(response.body.message || response.statusText)
        }
        dispatch.chat.addToFavourites(timelineRoomId)
        return { response, body }
      } catch (error) {
        console.log('[Chat/addTimelineToFavourites]', error)
      }
    },
    removeTimelineToFavourites: async timelineRoomId => {
      try {
        const { response, body } = await removeTimelineToFavourites(undefined, {
          timelineRoomId
        })
        if (!response.ok) {
          throw Error(response.body.message || response.statusText)
        }
        dispatch.chat.removeFromFavourites(timelineRoomId)
        return { response, body }
      } catch (error) {
        console.log('[Chat/removeTimelineToFavourites]', error)
      }
    },
    reportProblem: async payload => {
      const parsePayload = evolve({
        user_id: toInteger,
        reference_id: callOr(toInteger, null)
      })
      try {
        const { response, body } = await reportProblem({
          body: parsePayload(payload)
        })
        if (!response.ok) {
          throw Error(response.body.message || response.statusText)
        }
        return { response, body }
      } catch (error) {
        return { error }
      }
    },
    async checkUnreadMessages(_, rootState) {
      const { body } = await getStats()
      dispatch.chat.saveUnread(body)
    },
    parseTimelineEventSSE(message, rootState) {
      const data = headObj(message)
      const timelineRoomId = data?.timeline_room
      // Reverting fix for T16020 as we are creating side effect
      // Of not updating the timeline using SSE
      // Clean up later with proper fix
      // const sentByLoggedInUser = rootState.profile.id === data.created_by
      // if (!sentByLoggedInUser) {
      batch(() => {
        dispatch.chat.removePhantom({ timelineRoomId })
        delete headObj(message).is_read
        dispatch.chat.updateEvents(message)
        dispatch.chat.updateLastEvent(message)
      })
      // }
      const options = {
        body: pipe(headObj, path(['event_info', 'message']))(message)
      }
      const timelineRoom = pipe(headObj, prop('timeline_room'))(message)
      if ('Notification' in window && Notification.permission === 'granted' && !isMobile()) {
        const notification = new Notification('New message', options)
        notification.onclick = function (event) {
          event.preventDefault()
          window.location.replace(`${config.APP_PATH}/chat/${timelineRoom}`)
        }
      }
    },
    parseTimelineSSE(message, rootState) {
      const { is_read, timeline_room } = message
      const newApplication = rootState.chat?.timelines?.[message?.timeline_room]?.applications
      const newMessage = { ...message, applications: newApplication }
      const isUnread = is_read === '0'
      const searchState = rootState.chat.searchState

      batch(() => {
        const currentPath = window.location.pathname
        const currentRoom = currentPath.substring(currentPath.lastIndexOf('/') + 1)
        const isChatFocused = currentRoom === timeline_room
        if (shouldPatchTimeline(message, searchState)) {
          dispatch.chat.updateTimeline(newMessage)
          dispatch.chat.patchUsers(message?.users)
        }
        if (isUnread) {
          dispatch.chat.setUnread(message)
        }
        if (rootState.chat.currentTimelineId && isChatFocused) {
          dispatch.chat.setTimelineRead(rootState.chat.currentTimelineId)
        }
      })
    },
    parseMarkedReadSSE(message) {
      if (message && message.length === 0) return
      const messageObj = headObj(headObj(message).data)
      dispatch.chat.setTimelineEventsRead(message, messageObj.timeline_room)
    },
    parseUserUpdatedCallback(message, rootState) {
      const data = callbackUserSelector(message)
      dispatch.chat.patchUsers({ [data.id]: data })
      if (rootState.profile.id === data.id) {
        dispatch.session.setIdVerificationState(data.identity_verified_state)
      }
    },
    async startChat({ message, move_in_date, listingId }) {
      try {
        const { response, body } = await tenantStartChat(
          {
            message,
            move_in_date
          },
          {
            listingId
          }
        )
        if (!response.ok) {
          throw Error(response.body.message || response.statusText)
        }
        return { response, body }
      } catch (error) {
        return { error }
      }
    },
    async getTimelinesQueryString(payload) {
      const newFilterString = stringifySearchParams(payload)
      const { response, body } = await getTimelinesQueryString({ queryString: newFilterString })

      if (!response.ok) {
        throw Error(response.body.message || response.statusText)
      }
      batch(() => {
        if (payload && !isEmpty(payload)) dispatch.chat.setSearchState(payload)
        else dispatch.chat.clearSearchState()
        dispatch.chat.setTimelines({
          ...body,
          isSearch: body.is_search_result,
          page: 1,
          source: 'getTimelinesQueryString',
          intialTimelinesLoaded: true
        })
      })
      return { response, body }
    },
    async loadMoreTimelinesSearch(payload, rootState) {
      const page = rootState.chat.page
      const newFilterString = stringifySearchParams(payload)
      try {
        const { body, response } = await getNextTimelinesQueryString({
          queryString: newFilterString,
          pageNumber: page + 1
        })
        if (response.ok && isNotEmpty(body?.timelines)) {
          dispatch.chat.saveTimelines({
            ...body,
            page: page + 1,
            isSearch: body.is_search_result,
            intialTimelinesLoaded: true
          })
        }
        return { response, body }
      } catch (error) {
        console.log('[Chat/saveTimelineQueryString]', error)
      }
    },
    getCurrentTimeline(_, rootState) {
      return {
        id: rootState.chat.currentTimelineId,
        events: rootState.chat.events[rootState.chat.currentTimelineId]
      }
    },

    setTimelineToLatest(timelineRoom, rootState) {
      const lastEvent = rootState.chat.lastEvents[timelineRoom]
      batch(() => {
        dispatch.chat.clearChat(timelineRoom)
        dispatch.chat.updateEvents(lastEvent)
        dispatch.chat.updateLastEvent(lastEvent)
      })
    },

    async bulkTimelineMarkAsRead(payload) {
      try {
        const { response, body } = await bulkTimelineMarkAsRead(payload)
        if (!response.ok) {
          throw Error(response.body.message || response.statusText)
        }

        if (response.ok) {
          for (const [key, value] of Object.entries(body)) {
            // checking last_read is a mistake from BE. Remove it later
            if (key !== 'last_read') dispatch.chat.setRead(value)
          }
        }

        return { response, body }
      } catch (error) {
        console.log('[Chat/bulkTimelineMarkAsRead]', error)
      }
    },
    async bulkTimelineDelete(payload) {
      try {
        const { response, body } = await bulkTimelineDelete(payload)
        if (!response.ok) {
          throw Error(response.body.message || response.statusText)
        }

        if (response.ok) {
          for (const [key, value] of Object.entries(body)) {
            if (key === 'deleted') {
              value.forEach(item => {
                const timelineRoomId = Object.values(item.data)?.[0]?.timeline_room
                if (timelineRoomId) {
                  dispatch.chat.remove(['timelines', timelineRoomId])
                }
              })
            }
          }
        }

        return { response, body }
      } catch (error) {
        console.log('[Chat/bulkTimelineDelete]', error)
      }
    },
    async loadTimeline(payload, rootState) {
      if (rootState.chat.timelines?.[payload.timelineId]) {
        if (payload.timelineId === rootState.chat?.current_timeline?.timeline_room) return
        return dispatch.chat.setCurrentTimeline({
          ...rootState.chat.timelines[payload.timelineId],
          is_search_result: payload.isSearchResult
        })
      }
      const { response, body } = await loadTimeline({ timelineId: payload.timelineId })

      if (!response.ok) {
        throw Error(response.body.message || response.statusText)
      }
      batch(() => {
        dispatch.chat.setCurrentTimeline({
          ...body.timelines[payload.timelineId],
          is_search_result: payload.isSearchResult
        })
        if (payload.isSearchResult) {
          delete body.timeline_events2
        }
        dispatch.chat.saveTimelines({
          ...omitTimelines(body), // ommiting timelines for the following usecase: reload on http://local-web.machobear.ca:3000/chat/4213?filter=inquiries
          isSearch: payload.isSearchResult,
          page: 1,
          intialTimelinesLoaded: true,
          source: 'loadTimeline'
        })
      })
    },
    async loadInitialSearchEvents(payload, rootState) {
      if (payload.targetMessageId) {
        if (!rootState.chat.eventsSearch?.[payload.timelineId]) return
        const initialEvent = {
          timelineId: payload.timelineId,
          events: {
            [payload.targetMessageId]:
              rootState.chat.eventsSearch[payload.timelineId][payload.targetMessageId]
          }
        }
        dispatch.chat.replaceEvents(initialEvent)
      }

      await Promise.all([
        dispatch.chat.getEventsSearchUp({
          timelineRoomId: payload.timelineId,
          lastEventId: payload.targetMessageId
        }),
        dispatch.chat.getEventsSearchDown({
          timelineRoomId: payload.timelineId,
          lastEventId: payload.targetMessageId
        })
      ])
    }
  }),

  selectors: selectorBuilder(slice => ({
    timelines() {
      return timelinesSelector
    },
    timelinesSearch() {
      return timelinesSearchSelector
    },
    timeline() {
      return timelineSelector
    },
    timelineEvents() {
      return slice(timelineEventsSelector)
    },
    listingTimelines() {
      return listingTimelinesSelector
    },
    tenantTimelines() {
      return tenantTimelinesSelector
    }
  }))
}

export default chat

function shouldPatchTimeline(message, searchState = {}) {
  if (searchState.search) return false
  if (searchState.unread && message.is_read === '1') return false
  if (searchState.filter && !checkFilter(message, searchState.filter)) return false
  if (searchState.favourite && message.is_favourite !== '1') return false
  return true
}

function checkFilter(message, filter) {
  if (filter === 'inquiries' && message.state_machine !== 'active') return false
  if (filter === 'tenants' && message.state_machine !== 'rented') return false
  if (filter === 'archived' && message.state_machine !== 'history') return false
  return true
}
